import type {
  LeadMatch,
  LeadMatchState,
  MatchReason
} from "../../../Leads/api/services.types.ts"


/**
 * One set of strings per concept: the wizard panel and the import report both
 * say why a row matched, so a reworded reason has to reach both.
 */
const REASON_LABEL: Record<MatchReason, string> = {
  email: "leadMatch.reasonEmail",
  phone: "leadMatch.reasonPhone",
  name: "leadMatch.reasonName"
}

/**
 * A Strong match is an exact handle; a Weak match is a shared name, which is
 * evidence and not identity. They have to differ at a glance, or a manager
 * abandons a real new job over two people who happen to share a name.
 */
const REASON_COLOUR: Record<MatchReason, string> = {
  email: "gold",
  phone: "gold",
  name: "default"
}

/**
 * The server sends a key and which vocabulary it belongs to, never a sentence.
 * The two overlap — `won` is both a stage and an inactive reason — which is
 * why the kind decides the table rather than the key alone.
 */
function matchStateKey(state: LeadMatchState | null): string | null {
  if (!state?.key) return null
  return `leadMatch.state.${state.kind}.${state.key}`
}

const MS_PER_DAY = 86_400_000

function ageInDays(created: string | null): number | null {
  if (!created) return null
  const at = new Date(created).getTime()
  if (Number.isNaN(at)) return null
  return Math.max(0, Math.floor((Date.now() - at) / MS_PER_DAY))
}

/**
 * A row the caller may not open carries no id, and that absence is the whole
 * statement — there is no second flag that could disagree with it.
 */
function isReachable(match: LeadMatch): boolean {
  return !!match.id
}

/**
 * The state as a word, or nothing. The repos deploy apart, so the server can
 * send a state this build has no word for — saying nothing beats printing the
 * key path in the field a row leads with.
 */
function matchStateLabel(
  state: LeadMatchState | null,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  const key = matchStateKey(state)
  if (!key) return null
  return t(key, { defaultValue: "" }) || null
}

/** How long ago a row's lead was captured, as a word. */
function matchAgeLabel(
  created: string | null,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  const days = ageInDays(created)
  if (days === null) return null
  return days === 0
    ? t("leadWizard.leadDuplicate.createdToday")
    : t("leadWizard.leadDuplicate.createdDaysAgo", { count: days })
}

export {
  REASON_COLOUR,
  REASON_LABEL,
  ageInDays,
  isReachable,
  matchAgeLabel,
  matchStateKey,
  matchStateLabel
}
